import { atom } from 'jotai';

/**
 * TTS播放器状态
 * 整合了所有TTS相关的状态，实现单一数据源
 */

// 音频数据项的类型定义
export interface VoiceItem {
  voiceList: Array<{
    data?: string;
    error?: any;
    message: string;
    addition?: { frontend?: string };
    [key: string]: any;
  }>;
  duration?: number;
  slideId?: string;
  taskId?: number; // taskId是number类型
  length?: number; // 添加length属性
  [key: string]: any;
}

// 当前播放项的类型定义
export interface CurrentPlaying {
  sid: string;
  index: number;
  firstPlay?: boolean;
}

/**
 * TTS播放器整合状态
 * 包含：播放队列、当前播放、音频数据、播放状态、字幕等
 */
export const ttsPlayerAtom = atom<{
  // 播放队列
  playTasks: string[];

  // 当前播放
  currentPlaying: CurrentPlaying | null;

  // 音频数据（key为sid）
  voiceData: Record<string, VoiceItem>;

  // 播放状态
  isPlaying: boolean;
  gottenAudio: boolean;

  // 字幕显示
  displayMessage: string;

  // 语音配置
  isVoiceEnabled: boolean;
  voiceConfig?: any;
  voiceRatio: number;
}>({
  // 播放队列
  playTasks: [],

  // 当前播放
  currentPlaying: null,

  // 音频数据
  voiceData: {},

  // 播放状态
  isPlaying: false,
  gottenAudio: false,

  // 字幕显示
  displayMessage: '',

  // 语音配置
  isVoiceEnabled: true,
  voiceConfig: undefined,
  voiceRatio: 0,
});

// ==================== 向后兼容的Derived Atom ====================
/**
 * 为了保持向后兼容，导出derived atom
 * 现有代码无需改动，可以继续使用这些atom
 */

// 音频数据（可写）
export const voiceDataAtom = atom(
  (get) => get(ttsPlayerAtom).voiceData,
  (get, set, newValue: Record<string, VoiceItem> | ((prev: Record<string, VoiceItem>) => Record<string, VoiceItem>)) => {
    const current = get(ttsPlayerAtom);
    const updatedVoiceData =
      typeof newValue === 'function'
        ? newValue(current.voiceData)
        : newValue;
    set(ttsPlayerAtom, { ...current, voiceData: updatedVoiceData });
  }
);

// 播放任务队列
export const playTasksAtom = atom(
  (get) => get(ttsPlayerAtom).playTasks,
  (
    get,
    set,
    newValue: string[] | ((prev: string[]) => string[])
  ) => {
    const current = get(ttsPlayerAtom);
    const updatedPlayTasks =
      typeof newValue === 'function'
        ? newValue(current.playTasks)
        : newValue;
    set(ttsPlayerAtom, { ...current, playTasks: updatedPlayTasks });
  }
);

// 当前播放
export const currentPlayingAtom = atom(
  (get) => get(ttsPlayerAtom).currentPlaying,
  (
    get,
    set,
    newValue: CurrentPlaying | null | ((prev: CurrentPlaying | null) => CurrentPlaying | null)
  ) => {
    const current = get(ttsPlayerAtom);
    const updatedCurrentPlaying =
      typeof newValue === 'function'
        ? newValue(current.currentPlaying)
        : newValue;
    set(ttsPlayerAtom, { ...current, currentPlaying: updatedCurrentPlaying });
  }
);

// 是否开启语音
export const isVoiceAtom = atom(
  (get) => get(ttsPlayerAtom).isVoiceEnabled,
  (get, set, newValue: boolean | ((prev: boolean) => boolean)) => {
    const current = get(ttsPlayerAtom);
    const updatedIsVoiceEnabled =
      typeof newValue === 'function'
        ? newValue(current.isVoiceEnabled)
        : newValue;
    set(ttsPlayerAtom, { ...current, isVoiceEnabled: updatedIsVoiceEnabled });
  }
);

// 语音配置
export const voiceConfigAtom = atom(
  (get) => get(ttsPlayerAtom).voiceConfig,
  (get, set, newValue: any) => {
    const current = get(ttsPlayerAtom);
    set(ttsPlayerAtom, { ...current, voiceConfig: newValue });
  }
);

// 语音比例
export const voiceRatioAtom = atom(
  (get) => get(ttsPlayerAtom).voiceRatio,
  (get, set, newValue: number | ((prev: number) => number)) => {
    const current = get(ttsPlayerAtom);
    const updatedVoiceRatio =
      typeof newValue === 'function'
        ? newValue(current.voiceRatio)
        : newValue;
    set(ttsPlayerAtom, { ...current, voiceRatio: updatedVoiceRatio });
  }
);

/**
 * 使用示例：
 *
 * // 新代码 - 使用整合的atom
 * const ttsState = useAtomValue(ttsPlayerAtom);
 * const setTTSState = useSetAtom(ttsPlayerAtom);
 *
 * // 旧代码 - 继续使用derived atom（无需改动）
 * const voiceData = useAtomValue(voiceDataAtom);
 * const [playTasks, setPlayTasks] = useAtom(playTasksAtom);
 *
 * // 迁移到新atom
 * setTTSState(prev => ({
 *   ...prev,
 *   playTasks: [...prev.playTasks, newSid],
 *   currentPlaying: { sid: newSid, index: 0 }
 * }));
 */
